import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Compass, ArrowRight, Shield, MapPin } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import api from '../api';
import SEO from './SEO';
import bespokeBg from '../assets/bespoke_bg_v2.webp';

const CustomisedDestination = () => {
  const [isSubmittingCustom, setIsSubmittingCustom] = useState(false);
  const [customSubmitStatus, setCustomSubmitStatus] = useState(null);
  const [customTripData, setCustomTripData] = useState({
    name: '',
    email: '',
    phone: '',
    place_preference: '',
    description: '',
    budget: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCustomTripSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingCustom(true);
    try {
      await api.post('/custom-trip', customTripData);
      setCustomSubmitStatus('success');
      setCustomTripData({ name: '', email: '', phone: '', place_preference: '', description: '', budget: '' });
      setTimeout(() => setCustomSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Error submitting custom trip:', error);
      setCustomSubmitStatus('error');
    } finally {
      setIsSubmittingCustom(false);
    }
  };

  const handleWhatsAppContinue = () => {
    const whatsappNumber = "+919880232901";
    const text = `*New Custom Trip Request*
*Name:* ${customTripData.name || 'Not provided'}
*Email:* ${customTripData.email || 'Not provided'}
*Phone:* ${customTripData.phone || 'Not provided'}
*Preference:* ${customTripData.place_preference || 'Not provided'}
*Budget:* ${customTripData.budget || 'Not provided'}

*Description:* ${customTripData.description || 'Not provided'}`;

    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodedText}`, '_blank');
  };

  return (
    <div className="bg-white min-h-screen pt-24">
      <SEO 
        title="Custom Trip Planner" 
        description="Design your dream vacation with our interactive customized trip builder. Select destinations, budget, duration, and let us handcraft your travel itinerary with Sunshine Travels." 
        canonicalPath="/customised-destination" 
      />
      <section className="relative py-24 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src={bespokeBg}
            alt="Bespoke Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/70"></div>
          {/* Bottom White Fade */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-20 items-start">
            {/* Text Side */}
            <div className="lg:w-1/2 space-y-10 lg:sticky lg:top-32">
              <div className="space-y-6">
                <motion.span
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="inline-block px-4 py-1.5 rounded-full bg-orange-100 text-orange-600 text-[10px] font-bold uppercase tracking-widest border border-orange-200"
                >
                  Bespoke Travel Design
                </motion.span>
                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-4xl md:text-6xl font-serif font-bold text-slate-900 leading-snug tracking-wide"
                >
                  Handcraft Your <br />
                  <span className="text-orange-500 italic">Dream Journey</span>
                </motion.h1>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-slate-600 text-xl leading-relaxed max-w-lg font-medium"
                >
                  Every traveler is unique, and so should be their journey. Tell us your vision, and our experts will weave it into an unforgettable reality.
                </motion.p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                {[
                  { icon: Sparkles, title: "100% Personalised", desc: "No templates. Every detail is chosen for you." },
                  { icon: Compass, title: "Expert Knowledge", desc: "Access to places others can only dream of." },
                  { icon: Shield, title: "End-to-End Care", desc: "We manage everything from start to finish." },
                  { icon: MapPin, title: "Hidden Gems", desc: "Discover the world's most secret escapes." }
                ].map((item, idx) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + idx * 0.1 }}
                    className="space-y-3"
                  >
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-orange-500 shadow-xl border border-slate-100">
                      <item.icon size={24} />
                    </div>
                    <h4 className="text-slate-900 font-black text-xs uppercase tracking-widest">{item.title}</h4>
                    <p className="text-slate-500 text-sm font-medium leading-relaxed">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Form Side */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:w-1/2 w-full"
            >
              <div className="bg-white p-8 md:p-12 rounded-[3.5rem] border border-slate-100 shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)]">
                <div className="mb-10 text-center">
                  <h3 className="text-2xl font-serif font-bold text-slate-900 mb-2">Start Your Custom Itinerary</h3>
                  <p className="text-slate-400 text-sm font-medium">Fill in the details below and we'll be in touch.</p>
                </div>

                <form onSubmit={handleCustomTripSubmit} className="space-y-8">
                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-4">Where do you want to go?</label>
                    <input
                      required
                      type="text"
                      list="tourist-places"
                      value={customTripData.place_preference}
                      placeholder="e.g. Swiss Alps, Kerala Backwaters, or a Hidden Island"
                      className="w-full px-8 py-5 rounded-3xl bg-slate-50 border border-slate-100 text-slate-900 focus:ring-2 focus:ring-orange-500 transition-all outline-none placeholder:text-slate-300 font-medium"
                      onChange={(e) => setCustomTripData({ ...customTripData, place_preference: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-4">Full Name</label>
                      <input
                        required
                        type="text"
                        value={customTripData.name}
                        placeholder="John Doe"
                        className="w-full px-8 py-5 rounded-3xl bg-slate-50 border border-slate-100 text-slate-900 focus:ring-2 focus:ring-orange-500 transition-all outline-none placeholder:text-slate-300 font-medium"
                        onChange={(e) => setCustomTripData({ ...customTripData, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-4">Budget Range</label>
                      <select
                        required
                        value={customTripData.budget}
                        className="w-full px-8 py-5 rounded-3xl bg-slate-50 border border-slate-100 text-slate-900 focus:ring-2 focus:ring-orange-500 transition-all outline-none cursor-pointer font-medium appearance-none"
                        onChange={(e) => setCustomTripData({ ...customTripData, budget: e.target.value })}
                      >
                        <option value="">Select Budget</option>
                        <option value="Economy">Economy (Comfortable)</option>
                        <option value="Premium">Premium (High-End)</option>
                        <option value="Luxury">Luxury (Ultimate)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-4">Email Address</label>
                      <input
                        required
                        type="email"
                        value={customTripData.email}
                        placeholder="john@example.com"
                        className="w-full px-8 py-5 rounded-3xl bg-slate-50 border border-slate-100 text-slate-900 focus:ring-2 focus:ring-orange-500 transition-all outline-none placeholder:text-slate-300 font-medium"
                        onChange={(e) => setCustomTripData({ ...customTripData, email: e.target.value })}
                      />
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-4">Phone Number</label>
                      <input
                        required
                        type="tel"
                        value={customTripData.phone}
                        placeholder="10-digit number"
                        maxLength={10}
                        className="w-full px-8 py-5 rounded-3xl bg-slate-50 border border-slate-100 text-slate-900 focus:ring-2 focus:ring-orange-500 transition-all outline-none placeholder:text-slate-300 font-medium"
                        onChange={(e) => setCustomTripData({ ...customTripData, phone: e.target.value.replace(/\D/g, '') })}
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-4">Describe Your Dream Journey</label>
                    <textarea
                      required
                      value={customTripData.description}
                      placeholder="Tell us about your preferences, number of travelers, dates, and any special requests..."
                      rows="5"
                      className="w-full px-8 py-6 rounded-3xl bg-slate-50 border border-slate-100 text-slate-900 focus:ring-2 focus:ring-orange-500 transition-all outline-none placeholder:text-slate-300 font-medium resize-none"
                      onChange={(e) => setCustomTripData({ ...customTripData, description: e.target.value })}
                    ></textarea>
                  </div>

                  <div className="space-y-4 pt-4">
                    {(() => {
                      const isFormValid = customTripData.name && customTripData.email && customTripData.phone && customTripData.place_preference && customTripData.description && customTripData.budget;
                      return (
                        <>
                          <button
                            type="submit"
                            disabled={isSubmittingCustom || !isFormValid}
                            className="w-full py-6 bg-slate-900 hover:bg-orange-500 text-white rounded-3xl font-black uppercase tracking-[0.3em] text-[10px] transition-all shadow-2xl flex items-center justify-center gap-3 disabled:opacity-50 group"
                          >
                            {isSubmittingCustom ? 'Submitting...' : isFormValid ? 'Send Request' : 'Enter the above details to proceed'}
                            {isFormValid && <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />}
                          </button>

                          <button
                            type="button"
                            onClick={handleWhatsAppContinue}
                            disabled={!isFormValid}
                            className="w-full py-6 bg-[#25D366] hover:bg-[#1eb956] text-white rounded-3xl font-black uppercase tracking-[0.3em] text-[10px] transition-all shadow-xl flex items-center justify-center gap-3 group disabled:opacity-50"
                          >
                            {isFormValid ? 'Chat on WhatsApp' : 'Enter the above details to proceed'}
                            {isFormValid && <FaWhatsapp size={20} className="group-hover:scale-110 transition-transform" />}
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {customSubmitStatus && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-10 left-1/2 -translate-x-1/2 px-10 py-5 rounded-3xl shadow-2xl z-50 font-black uppercase tracking-widest text-[10px] ${customSubmitStatus === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}
          >
            {customSubmitStatus === 'success' ? 'Request Sent! We will contact you shortly.' : 'Error sending request. Please try again.'}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomisedDestination;
